import { useEffect, useRef, useState } from 'react'
import type { ChangeEvent, KeyboardEvent } from 'react'
import { useLocation } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'
import {
    AI_ATTACHMENT_ACCEPT,
    AiChatAuthError,
    AiChatConfigError,
    AiChatRequestError,
    checkAiAttachment,
    downloadAiExport,
    fetchHrAiAssistEnabled,
    isAiExportLink,
    linkifyAiExports,
    resetAiChat,
    streamAiChatMessage,
    streamAiChatMessageWithFile,
    submitAiFeedback,
} from './aiChatService'
import type {
    AiChatButton,
    AiChatStreamHandlers,
    AiReplyRating,
} from './aiChatService'
import { useSpeechInput } from './useSpeechInput'
import { useReadAloud } from './useReadAloud'
import { ReplyActions } from './ReplyActions'
import './aiChatWidget.css'

type ChatMessage = {
    id: string
    from: 'user' | 'bot'
    text: string
    buttons?: AiChatButton[]
    error?: boolean
    /** Epoch ms when the message was sent/received; absent on older saved history. */
    at?: number
    /** The user's thumbs up / down on a bot reply. */
    rating?: AiReplyRating
    /** Name of the file sent with a user message. */
    attachment?: string
}

// Example prompts shown on an empty chat. The agent currently has no tools or
// user context, so these are how-to questions rather than "my data" lookups.
const SUGGESTIONS = [
    'How do I apply for leave?',
    'How do I raise a permission request?',
    'How do I request an edit to my profile?',
]

// Routes that already are a full chat screen - no floating widget there.
const HIDDEN_ON = ['/mybot']

const storageKey = (empCode: string) => `aiChatWidget:${empCode}`
const conversationKey = (empCode: string) => `aiChatWidget:${empCode}:conv`

const loadConversationId = (empCode: string): string | null => {
    try {
        return sessionStorage.getItem(conversationKey(empCode))
    } catch {
        return null
    }
}
type Bounds = { top: number; left: number }

// Page content area = below the app header and right of the side nav.
const measureContentArea = (): Bounds => {
    const header = document.querySelector('.header')
    const sideNav = document.querySelector('.side-nav')
    const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0
    const navRect = sideNav?.getBoundingClientRect()
    const left = navRect && navRect.width > 0 ? Math.max(0, navRect.right) : 0
    return { top, left }
}

// "10:42 AM" for today, "7 Oct, 10:42 AM" for earlier days.
const formatTime = (at: number) => {
    const d = new Date(at)
    const time = d.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
    })
    if (d.toDateString() === new Date().toDateString()) return time
    return `${d.toLocaleDateString([], { day: 'numeric', month: 'short' })}, ${time}`
}

let idSeq = 0
const nextId = () => `aic-${Date.now()}-${++idSeq}`

const loadHistory = (empCode: string): ChatMessage[] => {
    try {
        const raw = sessionStorage.getItem(storageKey(empCode))
        return raw ? (JSON.parse(raw) as ChatMessage[]) : []
    } catch {
        return []
    }
}

const SparkIcon = () => (
    <svg
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        viewBox="0 0 24 24"
    >
        <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
        <circle cx="12" cy="12" r="3.2" />
    </svg>
)

const SendIcon = () => (
    <svg
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
    >
        <path d="M5 12h14m-6-6 6 6-6 6" />
    </svg>
)

const CloseIcon = () => (
    <svg
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
    >
        <path d="M6 6l12 12M18 6 6 18" />
    </svg>
)

const ExpandIcon = () => (
    <svg
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
    >
        <path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" />
    </svg>
)

const CollapseIcon = () => (
    <svg
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
    >
        <path d="M20 10h-6V4M4 14h6v6M14 10l7-7M10 14l-7 7" />
    </svg>
)

const RefreshIcon = () => (
    <svg
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
    >
        <path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7" />
    </svg>
)

const MicIcon = () => (
    <svg
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
    >
        <rect height="12" rx="3" width="6" x="9" y="3" />
        <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </svg>
)

const PaperclipIcon = () => (
    <svg
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
    >
        <path d="m20.5 11.5-8.3 8.3a5.5 5.5 0 0 1-7.8-7.8l8.3-8.3a3.7 3.7 0 0 1 5.2 5.2l-8.3 8.3a1.8 1.8 0 0 1-2.6-2.6l7.6-7.6" />
    </svg>
)

const MAX_MESSAGE_LENGTH = 1000

const AiChatWidget = () => {
    const { pathname } = useLocation()
    const empCode = localStorage.getItem('empCode') ?? ''
    const firstName = (localStorage.getItem('firstName') ?? '').split(' ')[0]

    // Server-driven on/off switch (GET_HR_AI_ASST_ENABLED_FLAG). Null until the
    // call answers, so nothing renders while the flag is still unknown.
    const [enabled, setEnabled] = useState<boolean | null>(null)
    const [open, setOpen] = useState(false)
    const [messages, setMessages] = useState<ChatMessage[]>(() =>
        loadHistory(empCode),
    )
    // Server-side conversation (AIChatController memory); null = start new.
    const [conversationId, setConversationId] = useState<string | null>(() =>
        loadConversationId(empCode),
    )
    const [draft, setDraft] = useState('')
    const [isTyping, setIsTyping] = useState(false)
    // Id of the bot bubble currently receiving streamed text; null until the
    // first token arrives (the typing dots show until then).
    const [streamingId, setStreamingId] = useState<string | null>(null)
    // Popup by default; the header button expands it over the whole page
    // content area. Not persisted - every new page load starts as the popup.
    const [expanded, setExpanded] = useState(false)
    const [bounds, setBounds] = useState<Bounds>({ top: 0, left: 0 })

    const listRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const launcherRef = useRef<HTMLButtonElement>(null)
    // In-flight streamed reply, aborted if the widget unmounts (e.g. logout).
    const streamAbortRef = useRef<AbortController | null>(null)

    useEffect(() => () => streamAbortRef.current?.abort(), [])

    // Voice input: dictated words go into the draft as they are heard; the
    // user still reviews and sends. Hidden where the browser has no support.
    const speech = useSpeechInput((text) =>
        setDraft(text.slice(0, MAX_MESSAGE_LENGTH)),
    )

    // Read aloud: one reply at a time - server voices, browser voice as fallback.
    const readAloud = useReadAloud()
    const speakingId = readAloud.speakingId

    // File chosen with 📎, sent with the next message.
    const [attachment, setAttachment] = useState<File | null>(null)
    const [attachmentError, setAttachmentError] = useState('')
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Ask the API whether the assistant is switched on (cached per page load).
    useEffect(() => {
        let active = true
        fetchHrAiAssistEnabled().then((value) => {
            if (active) setEnabled(value)
        })
        return () => {
            active = false
        }
    }, [])

    // Keep the conversation across page navigation for this browser session.
    useEffect(() => {
        try {
            sessionStorage.setItem(
                storageKey(empCode),
                JSON.stringify(messages.slice(-100)),
            )
        } catch {
            // storage full/blocked - history just won't survive a reload
        }
    }, [messages, empCode])

    useEffect(() => {
        try {
            if (conversationId) {
                sessionStorage.setItem(conversationKey(empCode), conversationId)
            } else {
                sessionStorage.removeItem(conversationKey(empCode))
            }
        } catch {
            // storage blocked - follow-ups still work until the page reloads
        }
    }, [conversationId, empCode])

    useEffect(() => {
        listRef.current?.scrollTo({
            top: listRef.current.scrollHeight,
            behavior: 'smooth',
        })
    }, [messages, isTyping, open])

    useEffect(() => {
        if (open) inputRef.current?.focus()
    }, [open, expanded])

    // Track the content area while expanded: window resizes and the side nav
    // collapsing/expanding (its width animates) both move the edges.
    useEffect(() => {
        if (!open || !expanded) return
        const update = () => setBounds(measureContentArea())
        update()
        window.addEventListener('resize', update)
        const observer =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(update)
                : null
        document
            .querySelectorAll('.header, .side-nav')
            .forEach((el) => observer?.observe(el))
        return () => {
            window.removeEventListener('resize', update)
            observer?.disconnect()
        }
    }, [open, expanded, pathname])

    if (!enabled) return null

    if (!empCode || HIDDEN_ON.includes(pathname)) return null

    const stopSpeaking = readAloud.stop
    const toggleSpeech = readAloud.toggle

    /** The user message a reply answered: the nearest one before it. */
    const questionBefore = (index: number): string => {
        for (let i = index - 1; i >= 0; i--) {
            if (messages[i].from === 'user') return messages[i].text
        }
        return ''
    }

    // Thumbs up / down: shown at once, saved for HR to review; undone if the
    // save fails so the icon never claims feedback that wasn't recorded.
    const rateReply = (
        m: ChatMessage,
        index: number,
        rating: AiReplyRating,
    ) => {
        if (m.rating === rating) return
        const previous = m.rating
        const setRating = (value?: AiReplyRating) =>
            setMessages((prev) =>
                prev.map((x) => (x.id === m.id ? { ...x, rating: value } : x)),
            )
        setRating(rating)
        submitAiFeedback({
            messageRef: m.id,
            rating,
            conversationId,
            question: questionBefore(index),
            reply: m.text,
        }).catch((err) => {
            console.error('AI chat feedback failed:', err)
            setRating(previous)
        })
    }

    const close = () => {
        speech.cancel()
        stopSpeaking()
        setOpen(false)
        launcherRef.current?.focus()
    }

    // 📎 Attach: checked here for type and size (the API checks again), then
    // sent with the next message.
    const onPickFile = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        e.target.value = '' // so choosing the same file again still fires
        if (!file) return
        const problem = checkAiAttachment(file)
        setAttachment(problem ? null : file)
        setAttachmentError(problem ?? '')
        inputRef.current?.focus()
    }

    const send = async (message: string, displayText?: string) => {
        const text = message.trim()
        const file = attachment
        if ((!text && !file) || isTyping) return

        setMessages((prev) => [
            ...prev,
            {
                id: nextId(),
                from: 'user',
                text: displayText ?? text,
                at: Date.now(),
                attachment: file?.name,
            },
        ])
        // Stop dictation first so a late result can't refill the cleared box.
        speech.cancel()
        setDraft('')
        setAttachment(null)
        setAttachmentError('')
        setIsTyping(true)

        // The bot bubble is created on the first token and grows as more
        // arrive, so the user reads the reply while it is being generated.
        const botId = nextId()
        let received = ''
        const controller = new AbortController()
        streamAbortRef.current = controller

        try {
            const handlers: AiChatStreamHandlers = {
                signal: controller.signal,
                onConversationId: setConversationId,
                onToken: (chunk) => {
                    const first = received === ''
                    received += chunk
                    const sofar = received
                    if (first) {
                        setStreamingId(botId)
                        setMessages((prev) => [
                            ...prev,
                            {
                                id: botId,
                                from: 'bot',
                                text: sofar,
                                at: Date.now(),
                            },
                        ])
                    } else {
                        setMessages((prev) =>
                            prev.map((m) =>
                                m.id === botId ? { ...m, text: sofar } : m,
                            ),
                        )
                    }
                },
            }
            // With a file the API reads it first (OCR for scans/images), so
            // the typing dots may show a few seconds longer.
            await (file
                ? streamAiChatMessageWithFile(
                      text,
                      file,
                      conversationId,
                      handlers,
                  )
                : streamAiChatMessage(text, conversationId, handlers))

            if (!received.trim()) {
                setMessages((prev) => [
                    ...prev,
                    {
                        id: botId,
                        from: 'bot',
                        text: 'I don’t have an answer for that yet. Try rephrasing your question.',
                        at: Date.now(),
                    },
                ])
            }
        } catch (err) {
            if (controller.signal.aborted) return
            console.error('AI chat send failed:', err)
            setMessages((prev) => [
                ...prev,
                {
                    id: nextId(),
                    from: 'bot',
                    text:
                        err instanceof AiChatRequestError
                            ? err.message
                            : err instanceof AiChatConfigError
                              ? 'The assistant is not set up for this environment yet. Please contact your administrator.'
                              : err instanceof AiChatAuthError
                                ? 'Your session has expired. Please sign in again to use the assistant.'
                                : received
                                  ? 'The reply was interrupted. Please try again.'
                                  : 'The assistant is not reachable right now. Please try again in a moment.',
                    error: true,
                    at: Date.now(),
                },
            ])
        } finally {
            if (streamAbortRef.current === controller) {
                streamAbortRef.current = null
            }
            setStreamingId(null)
            setIsTyping(false)
        }
    }

    // New chat: forget the server-side history too, then start fresh.
    const newChat = () => {
        const previous = conversationId
        streamAbortRef.current?.abort()
        speech.cancel()
        stopSpeaking()
        setMessages([])
        setConversationId(null)
        inputRef.current?.focus()
        if (previous) {
            resetAiChat(previous).catch((err) =>
                console.error('AI chat reset failed:', err),
            )
        }
    }

    // A failed download is reported in the conversation, like a failed reply.
    const downloadExport = (path: string) => {
        downloadAiExport(path).catch((err: Error) => {
            console.error('AI chat download failed:', err)
            setMessages((prev) => [
                ...prev,
                {
                    id: nextId(),
                    from: 'bot',
                    text:
                        err instanceof AiChatAuthError
                            ? 'Your session has expired. Please sign in again to download the file.'
                            : err.message,
                    error: true,
                    at: Date.now(),
                },
            ])
        })
    }

    // Links in replies: export links download with the user's token (a plain
    // link can't send it); anything else opens in a new tab.
    const markdownComponents: Components = {
        a: ({ href, children }) =>
            isAiExportLink(href) ? (
                <a
                    className="aic-download"
                    href={href}
                    onClick={(e) => {
                        e.preventDefault()
                        downloadExport(href)
                    }}
                >
                    {children}
                </a>
            ) : (
                <a href={href} rel="noopener noreferrer" target="_blank">
                    {children}
                </a>
            ),
    }

    const onInputKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            send(draft)
        }
    }

    return (
        <div className="aic-root">
            {open && (
                <section
                    aria-label="AI assistant"
                    aria-modal="false"
                    className={`aic-panel${expanded ? ' aic-panel--expanded' : ''}`}
                    role="dialog"
                    style={
                        expanded
                            ? { top: bounds.top, left: bounds.left }
                            : undefined
                    }
                    onKeyDown={(e) => e.key === 'Escape' && close()}
                >
                    <header className="aic-header">
                        <span aria-hidden="true" className="aic-header-mark">
                            <SparkIcon />
                        </span>
                        <div className="aic-header-text">
                            <strong>HR Assistant</strong>
                            <span>
                                Ask about leave, attendance, policies and more
                            </span>
                        </div>
                        <button
                            aria-label={
                                expanded
                                    ? 'Restore assistant to a popup'
                                    : 'Expand assistant to fill the page'
                            }
                            aria-pressed={expanded}
                            className="aic-icon-btn"
                            title={expanded ? 'Restore' : 'Expand to page'}
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                        >
                            {expanded ? <CollapseIcon /> : <ExpandIcon />}
                        </button>
                        <button
                            aria-label="Start a new chat"
                            className="aic-icon-btn"
                            title="New chat"
                            type="button"
                            onClick={newChat}
                        >
                            <RefreshIcon />
                        </button>
                        <button
                            aria-label="Close assistant"
                            className="aic-icon-btn"
                            title="Close"
                            type="button"
                            onClick={close}
                        >
                            <CloseIcon />
                        </button>
                    </header>

                    <div
                        ref={listRef}
                        aria-live="polite"
                        className="aic-messages"
                    >
                        {messages.length === 0 && (
                            <div className="aic-empty">
                                <span
                                    aria-hidden="true"
                                    className="aic-empty-mark"
                                >
                                    <SparkIcon />
                                </span>
                                <h3>
                                    {firstName
                                        ? `Hi ${firstName}, how can I help?`
                                        : 'How can I help?'}
                                </h3>
                                <p>
                                    Ask a question, or start with one of these:
                                </p>
                                <div className="aic-suggestions">
                                    {SUGGESTIONS.map((s) => (
                                        <button
                                            key={s}
                                            className="aic-chip"
                                            type="button"
                                            onClick={() => send(s)}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {messages.map((m, index) => (
                            <div
                                key={m.id}
                                className={`aic-row aic-row--${m.from}`}
                            >
                                {m.at ? (
                                    <time
                                        className="aic-time"
                                        dateTime={new Date(m.at).toISOString()}
                                        title={new Date(m.at).toLocaleString()}
                                    >
                                        {formatTime(m.at)}
                                    </time>
                                ) : null}
                                <div
                                    className={`aic-bubble aic-bubble--${m.from}${m.error ? ' aic-bubble--error' : ''}${m.from === 'bot' && !m.error ? ' aic-bubble--md' : ''}`}
                                >
                                    {/* Model replies are Markdown; react-markdown
                                        does not render raw HTML. Export paths the
                                        model wrote as text become download links. */}
                                    {m.from === 'bot' && !m.error ? (
                                        <ReactMarkdown
                                            components={markdownComponents}
                                        >
                                            {linkifyAiExports(m.text)}
                                        </ReactMarkdown>
                                    ) : (
                                        <>
                                            {m.attachment && (
                                                <span className="aic-bubble-file">
                                                    📎 {m.attachment}
                                                </span>
                                            )}
                                            {m.text}
                                        </>
                                    )}
                                </div>
                                {m.buttons?.length ? (
                                    <div className="aic-suggestions aic-suggestions--inline">
                                        {m.buttons.map((b) => (
                                            <button
                                                key={`${m.id}-${b.title}`}
                                                className="aic-chip"
                                                disabled={isTyping}
                                                type="button"
                                                onClick={() =>
                                                    send(
                                                        b.payload || b.title,
                                                        b.title,
                                                    )
                                                }
                                            >
                                                {b.title}
                                            </button>
                                        ))}
                                    </div>
                                ) : null}
                                {m.from === 'bot' &&
                                    !m.error &&
                                    streamingId !== m.id && (
                                        <ReplyActions
                                            canSpeak
                                            rating={m.rating}
                                            retryDisabled={isTyping}
                                            speaking={speakingId === m.id}
                                            text={m.text}
                                            onRate={(rating) =>
                                                rateReply(m, index, rating)
                                            }
                                            onRetry={
                                                questionBefore(index)
                                                    ? () =>
                                                          send(
                                                              questionBefore(
                                                                  index,
                                                              ),
                                                          )
                                                    : undefined
                                            }
                                            onSpeak={(plain) =>
                                                toggleSpeech(m.id, plain)
                                            }
                                        />
                                    )}
                            </div>
                        ))}

                        {isTyping && !streamingId && (
                            <div
                                aria-label="Assistant is typing"
                                className="aic-row aic-row--bot"
                            >
                                <div className="aic-bubble aic-bubble--bot aic-typing">
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            </div>
                        )}
                    </div>

                    {speech.error && (
                        <p className="aic-voice-error" role="alert">
                            {speech.error}
                        </p>
                    )}
                    {attachmentError && (
                        <p className="aic-voice-error" role="alert">
                            {attachmentError}
                        </p>
                    )}
                    {attachment && (
                        <div className="aic-attachment">
                            <span aria-hidden="true">📎</span>
                            <span className="aic-attachment-name">
                                {attachment.name}
                            </span>
                            <span className="aic-attachment-size">
                                {Math.max(
                                    1,
                                    Math.round(attachment.size / 1024),
                                )}{' '}
                                KB
                            </span>
                            <button
                                aria-label={`Remove ${attachment.name}`}
                                className="aic-attachment-remove"
                                title="Remove"
                                type="button"
                                onClick={() => setAttachment(null)}
                            >
                                <CloseIcon />
                            </button>
                        </div>
                    )}
                    <form
                        className="aic-composer"
                        onSubmit={(e) => {
                            e.preventDefault()
                            send(draft)
                        }}
                    >
                        {/* Attach sits left of the text box; mic and send stay right. */}
                        <input
                            ref={fileInputRef}
                            accept={AI_ATTACHMENT_ACCEPT}
                            className="aic-file-input"
                            tabIndex={-1}
                            type="file"
                            onChange={onPickFile}
                        />
                        <button
                            aria-label="Attach a file (.xlsx, .pdf, .png, .jpg, up to 2 MB)"
                            className="aic-mic"
                            disabled={isTyping}
                            title="Attach a file (.xlsx, .pdf, .png, .jpg – up to 2 MB)"
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <PaperclipIcon />
                        </button>
                        <textarea
                            ref={inputRef}
                            aria-label="Message the assistant"
                            className="aic-input"
                            maxLength={MAX_MESSAGE_LENGTH}
                            placeholder={
                                speech.listening
                                    ? 'Listening… speak now'
                                    : attachment
                                      ? 'Ask about the file (optional)…'
                                      : 'Type your question…'
                            }
                            rows={1}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={onInputKeyDown}
                        />
                        {speech.supported && (
                            <button
                                aria-label={
                                    speech.listening
                                        ? 'Stop voice input'
                                        : 'Start voice input'
                                }
                                aria-pressed={speech.listening}
                                className={`aic-mic${speech.listening ? ' aic-mic--listening' : ''}`}
                                disabled={isTyping}
                                title={
                                    speech.listening
                                        ? 'Stop listening'
                                        : 'Speak your question'
                                }
                                type="button"
                                onClick={() => {
                                    if (speech.listening) {
                                        speech.stop()
                                    } else {
                                        speech.start(draft)
                                        inputRef.current?.focus()
                                    }
                                }}
                            >
                                <MicIcon />
                            </button>
                        )}
                        <button
                            aria-label="Send message"
                            className="aic-send"
                            disabled={
                                (!draft.trim() && !attachment) || isTyping
                            }
                            type="submit"
                        >
                            <SendIcon />
                        </button>
                    </form>
                    <p className="aic-footnote">
                        AI answers can be wrong — check important details with
                        HR.
                    </p>
                </section>
            )}

            <button
                ref={launcherRef}
                aria-expanded={open}
                aria-label={open ? 'Close AI assistant' : 'Open AI assistant'}
                className={`aic-launcher${open ? ' aic-launcher--open' : ''}${open && expanded ? ' aic-launcher--hidden' : ''}`}
                type="button"
                onClick={() => (open ? close() : setOpen(true))}
            >
                {open ? <CloseIcon /> : <SparkIcon />}
            </button>
        </div>
    )
}

export default AiChatWidget
